import { useMutation } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, ButtonProps, Menu, MenuItem, Tooltip } from "@mui/material";
import { gql } from "generated-graphql";
import { JobStatus, TierIiReportOrgStatus } from "generated-graphql/graphql";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useCallback, useRef, useState } from "react";
import { useDownloadReportPreview } from "routes/Customer/Chemicals/Report/useDownloadReportPreview";
import { downloadFromUrl } from "util/download";

import { useAlerts } from "./Alerts/AlertProvider";
import { LongPollingJobWrapper, PollingResult } from "./LongPollingJobWrapper";

const GENERATE_T2S_MUTATION = gql(`
  mutation GenerateT2S($reportId: ID!) {
    generateT2S(reportId: $reportId)
  }
`);

export function GeneratePdfButton({
  reportId,
  label,
  onComplete,
  includeTierIISubmit,
  reportOrgStatus,
  ...buttonProps
}: {
  reportId: string;
  label?: string;
  /**
   * Callback to be called when the PDF is generated and downloaded.
   */
  onComplete?: () => void;
  /**
   * If true, includes "Tier II Submit" option in dropdown (for fulfillment page)
   */
  includeTierIISubmit?: boolean;
  /**
   * Organization status of the report, used to determine if T2S is enabled
   */
  reportOrgStatus?: TierIiReportOrgStatus;
} & ButtonProps) {
  const { downloadReportPreview } = useDownloadReportPreview();
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [reportPdfS3Key, setReportPdfS3Key] = useState<string | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { fetchFile } = useCheckForFile(reportPdfS3Key);
  const alerts = useAlerts();
  const downloadTriggeredRef = useRef<string | undefined>(undefined);

  const [mutateT2S, { loading: isGeneratingT2S }] = useMutation(GENERATE_T2S_MUTATION, {
    onCompleted({ generateT2S }) {
      handleMenuClose();
      downloadFromUrl(generateT2S);
    },
    onError(error) {
      alerts.error("We encountered an error generating T2S", error);
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleT2SClick = useCallback(async () => {
    await mutateT2S({ variables: { reportId } });
  }, [mutateT2S, reportId]);

  const handleDownloadClick = useCallback(
    async (includeUnderThreshold: boolean) => {
      handleMenuClose();
      // clear old job state
      setJobId(undefined);
      setReportPdfS3Key(undefined);
      downloadTriggeredRef.current = undefined; // Reset guard for new job

      const result = await downloadReportPreview(reportId, includeUnderThreshold);
      setReportPdfS3Key(result?.s3Key);
      setJobId(result?.jobId);
    },
    [downloadReportPreview, reportId]
  );

  const pollingCompleted = useCallback(
    async (result: PollingResult) => {
      // Guard: Only allow download once per jobId
      // This is kinda hacky but it prevents the file from opening twice.
      if (jobId && downloadTriggeredRef.current === jobId) {
        return;
      }
      if (jobId) {
        downloadTriggeredRef.current = jobId;
      }

      if (!result.pollingTimedOut && result.job?.status === JobStatus.Failed) {
        alerts.error("Failed to generate PDF");
        onComplete?.();
        return;
      }

      if (result.pollingTimedOut) {
        alerts.error("Polling for PDF generation job status timed out");
        onComplete?.();
        return;
      }

      if (result.job?.status === JobStatus.Succeeded) {
        if (result.job.output.downloadKeys && result.job.output.downloadKeys.length) {
          for (const s3Key of result.job.output.downloadKeys) {
            const signedUrl = await fetchFile(s3Key);
            if (signedUrl) {
              alerts.success("Preview ready, downloading...");
              // The signed URL downloads rather than opening a tab, so there is
              // no popup for a blocker to eat and no fallback link to offer.
              downloadFromUrl(signedUrl);
              onComplete?.();
              return;
            }
          }
        }
      }

      alerts.error("Failed to generate PDF. File not found");
    },
    [jobId, alerts, onComplete, fetchFile]
  );

  const isMenuOpen = Boolean(anchorEl);
  const shouldDisableT2S =
    (reportOrgStatus !== TierIiReportOrgStatus.Verified &&
      reportOrgStatus !== TierIiReportOrgStatus.NotReporting) ||
    isGeneratingT2S;

  return (
    <LongPollingJobWrapper
      key={jobId || "initial"}
      jobId={jobId}
      pollingCompleted={pollingCompleted}
      timeoutInSeconds={1200}
    >
      <>
        <Tooltip title="Download a PDF of your report">
          <Button
            startIcon={<Download />}
            endIcon={buttonProps.endIcon ? undefined : <ExpandMoreIcon />}
            color="primary"
            size="small"
            onClick={handleMenuOpen}
            {...buttonProps}
          >
            {label ?? "Report Preview"}
          </Button>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          {includeTierIISubmit && (
            <MenuItem onClick={handleT2SClick} disabled={shouldDisableT2S}>
              Tier II Submit
            </MenuItem>
          )}
          <MenuItem onClick={() => handleDownloadClick(false)}>
            Only chemicals above threshold
          </MenuItem>
          <MenuItem onClick={() => handleDownloadClick(true)}>
            Include chemicals below threshold
          </MenuItem>
        </Menu>
      </>
    </LongPollingJobWrapper>
  );
}
